import { logger } from '@/utils/logger';
import CategoryBadge from '@/components/shared/CategoryBadge';
import { AIAutomationDialog } from '@/components/rules/dialogs/AIAutomationDialog';
import { useBackBlocker } from '@/hooks/use-back-blocker';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import useBoolean from '@/hooks/use-boolean';
import useCategories from '@/hooks/use-categories';
import { uiStateActions, uiStateProxy } from '@/state/ui-state';
import {
  ActionType,
  formatCurrency,
  formatDate,
  Status,
  TriggerType,
} from '@polybank/shared';
import {
  Calendar,
  Clock,
  CreditCard,
  DollarSign,
  Receipt,
  Tag,
  Zap,
} from 'lucide-react';
import { useAnalytics } from '@/context/analytics-context';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTrackedSnapshot } from 'valtio-select';
import {
  CreateRuleDialog,
  RulePresets,
} from '@/components/rules/dialogs/CreateRuleDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import useTransaction from '@/hooks/use-transaction';
import { askConfirm } from '@/components/shared/ConfirmationDialog';
import useRules from '@/hooks/use-rules';
import { FormattedHumanReadableRule } from '@/components/rules/components/FormattedHumanReadableRule';
import { CategoryEditModal } from '@/components/categories/CategoryEditModal';
import Skeleton from 'react-loading-skeleton';

const TransactionDetailModal = memo(() => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const categoriesMap = useCategories().categoriesMap;

  const [
    isTransactionCategoryEditorOpen,
    { setFalse: closeTransactionCategoryEditor },
  ] = useBoolean(false);
  const [
    showAIAutomation,
    { setTrue: showAiAutomation, setValue: setShowAIAutomation },
  ] = useBoolean(false);

  const transactionIdForDetails = useTrackedSnapshot(
    uiStateProxy,
    (state) => state.transactionIdForDetails
  );
  const { transaction: transactionForDetails, isLoading } = useTransaction(
    transactionIdForDetails!
  );

  const categoryName = transactionForDetails?.category_id
    ? categoriesMap[transactionForDetails?.category_id]?.name
    : '';
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    categoryName || ''
  );
  const [showCreateRule, setShowCreateRule] = useState(false);
  const [showEditRuleDialog, setShowEditRuleDialog] = useState(false);
  const { bankAccountsMap } = useBankAccounts();

  useEffect(() => {
    if (transactionIdForDetails) {
      trackEvent('transaction_detail_opened', {});
    }
  }, [transactionIdForDetails, trackEvent]);

  const appliedRuleId = transactionForDetails?.applied_rules?.[0];
  logger.debug('appliedRuleId', appliedRuleId);
  const { rulesMap } = useRules();
  const appliedRule = appliedRuleId ? rulesMap[appliedRuleId] : undefined;
  useBackBlocker({
    enabled: !!transactionForDetails && !showAIAutomation,
    onBack: () => {
      uiStateActions.setTransactionIdForDetails(undefined);
    },
  });

  // Pre-filled data for rule creation
  const rulePresets: RulePresets | undefined = useMemo(() => {
    if (!transactionForDetails) return undefined;
    return {
      title: `Rule created from ${transactionForDetails.description}`,
      triggers: [
        {
          triggerType: TriggerType.DESCRIPTION_CONTAINS,
          triggerValue: transactionForDetails.description,
        },
        {
          triggerType: TriggerType.AMOUNT_EQUAL,
          triggerValue: transactionForDetails.amount.toString(),
        },
        {
          triggerType: TriggerType.ACCOUNT_IS,
          triggerValue: transactionForDetails.bank_account_id!,
        },
      ],
      actions: [
        {
          actionType: ActionType.SET_CATEGORY,
          actionValue: selectedCategory,
        },
      ],
    };
  }, [transactionForDetails, selectedCategory]);

  // Pre-filled data for editing existing rule
  const editRulePresets: RulePresets | undefined = useMemo(() => {
    if (!appliedRule?.$id) return undefined;
    return {
      id: appliedRule.$id,
      title:
        appliedRule.title || `Rule for ${transactionForDetails?.description}`,
    };
  }, [appliedRule, transactionForDetails?.description]);

  const handleCategoryUpdated = useCallback(
    async (newCategoryId?: string) => {
      setSelectedCategory(newCategoryId);
      closeTransactionCategoryEditor();
    },
    [closeTransactionCategoryEditor]
  );

  const handleCreateRule = useCallback(() => {
    if (appliedRuleId) {
      askConfirm({
        title: t('transactionDetail.createRule'),
        description: t('transactionDetail.ruleExistsWarning'),
        onConfirm: () => {
          setShowAIAutomation(true);
        },
      });
    } else {
      setShowAIAutomation(true);
    }
  }, [appliedRuleId, setShowAIAutomation, t]);

  const handleEditRule = useCallback(() => {
    setShowEditRuleDialog(true);
  }, []);

  const getTypeColor = useCallback((type: string) => {
    switch (type) {
      case 'income':
        return 'text-green-600';
      case 'expense':
        return 'text-red-600';
      default:
        return 'text-muted-foreground';
    }
  }, []);

  const getTypeIcon = useCallback((type: string) => {
    switch (type) {
      case 'income':
        return '↗';
      case 'expense':
        return '↙';
      default:
        return '→';
    }
  }, []);

  if (!transactionIdForDetails) return null;

  return (
    <>
      <Dialog
        open={!!transactionIdForDetails}
        onOpenChange={(open) => {
          if (!open) {
            uiStateActions.setTransactionIdForDetails(undefined);
          }
        }}
      >
        <DialogContent className='max-w-xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Receipt className='w-5 h-5' />
              {t('transactionDetail.title')}
            </DialogTitle>
          </DialogHeader>

          {isLoading || !transactionForDetails ? (
            <div className='space-y-4'>
              {/* Skeleton: Header area */}
              <div className='flex items-start justify-between p-4 bg-muted/30 rounded-lg'>
                <div className='space-y-2 flex-1'>
                  <Skeleton width={200} height={20} />
                  <Skeleton width={120} height={14} />
                </div>
                <Skeleton width={100} height={28} />
              </div>

              {/* Skeleton: Details grid */}
              <div className='grid grid-cols-2 gap-3'>
                <div className='space-y-1'>
                  <Skeleton width={80} height={12} />
                  <Skeleton width={140} height={16} />
                </div>
                <div className='space-y-1'>
                  <Skeleton width={60} height={12} />
                  <Skeleton width={80} height={24} borderRadius={12} />
                </div>
              </div>

              {/* Skeleton: Category section */}
              <div className='space-y-2'>
                <Skeleton width={100} height={16} />
                <Skeleton width={120} height={28} borderRadius={12} />
              </div>

              <Separator className='my-4' />

              {/* Skeleton: Rules section */}
              <div className='p-4 rounded-lg'>
                <Skeleton width='100%' height={80} borderRadius={8} />
              </div>
            </div>
          ) : (
            <div className='space-y-4'>
              {/* Transaction Header - Compact */}
              <div className='flex items-start justify-between p-4 bg-muted/30 rounded-lg'>
                <div className='space-y-1 flex-1'>
                  <h3 className='font-semibold'>
                    {transactionForDetails.description}
                  </h3>
                  <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                    <Calendar className='w-3 h-3' />
                    <span>
                      {formatDate(transactionForDetails.transaction_date)}
                    </span>
                  </div>
                </div>
                <div className='text-right'>
                  <div
                    className={`text-xl font-bold ${getTypeColor(transactionForDetails.transaction_type)}`}
                  >
                    {getTypeIcon(transactionForDetails.transaction_type)}{' '}
                    {formatCurrency(transactionForDetails.amount)}
                  </div>
                  {transactionForDetails.status === Status.PENDING && (
                    <Badge variant='outline' className='mt-1 text-xs'>
                      <Clock className='w-3 h-3 mr-1' />
                      {t('transactionDetail.pending')}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Compact Details Grid */}
              <div className='grid grid-cols-2 gap-3'>
                <div className='space-y-1'>
                  <div className='text-xs font-medium text-muted-foreground flex items-center gap-1'>
                    <CreditCard className='w-3 h-3' />
                    {t('transactionDetail.account')}
                  </div>
                  <div className='text-sm'>
                    {
                      bankAccountsMap[transactionForDetails.bank_account_id!]
                        ?.account_name
                    }
                  </div>
                </div>

                <div className='space-y-1'>
                  <div className='text-xs font-medium text-muted-foreground flex items-center gap-1'>
                    <DollarSign className='w-3 h-3' />
                    {t('transactionDetail.type')}
                  </div>
                  <Badge
                    variant='outline'
                    className={`text-xs ${getTypeColor(transactionForDetails.transaction_type)}`}
                  >
                    {transactionForDetails.transaction_type
                      .charAt(0)
                      .toUpperCase() +
                      transactionForDetails.transaction_type.slice(1)}
                  </Badge>
                </div>
              </div>

              {/* Category Selection */}
              <div className='space-y-2'>
                <div className='text-sm font-medium flex items-center gap-2'>
                  <Tag className='w-4 h-4' />
                  {t('transactionDetail.category')}
                </div>
                {/* <CategoryPicker
                currentCategoryId={transaction.category_id}
                onCategoryChange={handleCategoryChange}
              /> */}
                <CategoryBadge
                  categoryId={transactionForDetails.category_id!}
                  transactionId={transactionForDetails.$id}
                  allowEdit={true}
                  onCategoryUpdated={handleCategoryUpdated}
                />
              </div>

              {/* Tags - Only show if they exist */}
              {/* {transaction.tags && transaction.tags.length > 0 && (
              <div className='space-y-2'>
                <div className='text-sm font-medium'>Tags</div>
                <div className='flex flex-wrap gap-1'>
                  {transaction.tags.map((tag) => (
                    <Badge key={tag} variant='secondary' className='text-xs'>
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )} */}

              <Separator className='my-4' />

              {/* Prominent Create Rule CTA or display current rule */}
              <div className='space-y-3'>
                {appliedRule?.humanReadable ? (
                  <FormattedHumanReadableRule
                    rawText={appliedRule?.humanReadable}
                    onEditRule={handleEditRule}
                    onCreateNewRule={handleCreateRule}
                  />
                ) : (
                  <div className='bg-gradient-to-r from-primary/5 to-primary/10 p-4 rounded-lg border border-primary/20'>
                    <div className='flex items-start gap-3'>
                      <div className='w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0'>
                        <Zap className='w-4 h-4 text-primary' />
                      </div>
                      <div className='flex-1'>
                        <h4 className='font-medium text-sm mb-1'>
                          {t('transactionDetail.automateTitle')}
                        </h4>
                        <p className='text-xs text-muted-foreground mb-3'>
                          {t('transactionDetail.automateDescription')}
                        </p>
                        <Button
                          onClick={handleCreateRule}
                          className='gap-2 w-full'
                          size='sm'
                        >
                          <Zap className='w-4 h-4' />
                          {t('transactionDetail.createAutomationRule')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Rule Dialog */}
      {showCreateRule && (
        <CreateRuleDialog
          open={showCreateRule}
          onOpenChange={setShowCreateRule}
          presets={rulePresets}
        />
      )}

      {!!showAIAutomation && transactionForDetails && (
        <AIAutomationDialog
          open={showAIAutomation}
          onOpenChange={setShowAIAutomation}
          transactionDescription={transactionForDetails.description}
          transactionAmount={Math.abs(transactionForDetails.amount)}
          transactionCategory={selectedCategory}
          transactionId={transactionForDetails.$id!}
          forceApply={true}
        />
      )}

      {showEditRuleDialog && appliedRule && (
        <CreateRuleDialog
          open={showEditRuleDialog}
          onOpenChange={setShowEditRuleDialog}
          presets={editRulePresets}
          rawRule={appliedRule.raw}
        />
      )}

      {!!isTransactionCategoryEditorOpen && transactionForDetails && (
        <CategoryEditModal
          isOpen={isTransactionCategoryEditorOpen}
          onClose={closeTransactionCategoryEditor}
          transactionId={transactionForDetails.$id!}
          currentCategoryId={transactionForDetails.category_id!}
          onCategoryUpdated={handleCategoryUpdated}
          onCreateRule={showAiAutomation}
        />
      )}
    </>
  );
});
TransactionDetailModal.displayName = 'TransactionDetailModal';
export { TransactionDetailModal };
